import React, { useEffect, useState } from 'react';
import { AttendanceResponse } from '../../interfaces/attendance.interface';
import { AttendanceService } from '../../services/attendance.service';
import { Box, Checkbox, Chip, FormControlLabel, Typography, Button, Grid, TextField } from '@mui/material';
import { format, isWithinInterval, parseISO, eachDayOfInterval, startOfDay, endOfDay, subDays } from 'date-fns';
import { useShiftLookup } from '../../hooks/useShiftLookup';
import { LeaveApplicationResponse, LeaveStatus } from '../../interfaces/leave.interface';
import { LeaveService } from '../../services/leave.service';
import { HolidayResponse } from '../../interfaces/holiday.interface';
import { HolidayService } from '../../services/holiday.service';
import { useToast } from '../../context/ToastContext';
import { InstitutionSettingsService } from '../../services/institutionSettings.service';
import { FALLBACK_WEEKEND } from '../../services/institutionSettings.service';
import { humanizeEnum } from '../../utils/format';
import { WeekDay } from '../../interfaces/institutionSettings.interface';
import { AuthService } from '../../services/auth.service';
import { WorkSiteService } from '../../services/workSite.service';
import { Column } from '../common/responsiveTable.types';
import ResponsiveTable from '../common/ResponsiveTable';
import AttendanceStatusChip from './AttendanceStatusChip';
import LocationStatusChip from './LocationStatusChip';

import SearchableSelect from '../common/SearchableSelect';
import { usePermission } from '../../hooks/usePermission';
type AttendanceRecord = AttendanceResponse['content'][number];

const JS_DAY_TO_NAME: WeekDay[] = ['SUNDAY', 'MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY'];

/** One rendered line: a date in range, plus the attendance record for it if there is one. */
export interface AttendanceRow {
    /** Raw yyyy-MM-dd key from `dateRange` — stable and unique, unlike the display-formatted `date`. */
    rawDate: string;
    date: string;
    day: string;
    status: string;
    attendance?: AttendanceRecord;
}

const formatTime = (dateString: string) => {
    if (!dateString) return '-';
    return format(parseISO(dateString), 'HH:mm');
};

/** Clock-in stamps endTime = startTime, so an equal end means "not clocked out yet". */
export const formatEndTime = (startStr: string, endStr: string): string => {
    if (!endStr || endStr === startStr) return '—';
    return formatTime(endStr);
};

const formatDuration = (startStr: string, endStr: string): string => {
    if (!startStr || !endStr) return '-';
    const diffMs = parseISO(endStr).getTime() - parseISO(startStr).getTime();
    if (diffMs <= 0) return '-';
    const totalMinutes = Math.floor(diffMs / 60000);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return `${hours}h ${minutes}m`;
};

const labelSx = { color: 'text.secondary', fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '.4px' } as const;

// Declared in the OLD table's source order (Date, Day, Start, End, Total, Status, Shift, Instance
// Status, Late, OT) — `priority` only picks the card/compact LEAD, it never reorders table mode.
export const attendanceColumns: Column<AttendanceRow>[] = [
    { key: 'date', header: 'Date', priority: 1, helpKey: 'attendance.myList.col.date', render: r => r.date },
    { key: 'day', header: 'Day', helpKey: 'attendance.myList.col.day', render: r => r.day },
    { key: 'startTime', header: 'Start Time', cardLabel: 'In', helpKey: 'attendance.myList.col.startTime', render: r => r.attendance ? formatTime(r.attendance.startTime) : '-' },
    { key: 'endTime', header: 'End Time', helpKey: 'attendance.myList.col.endTime', cardLabel: 'Out', render: r => r.attendance ? formatEndTime(r.attendance.startTime, r.attendance.endTime) : '-' },
    { key: 'totalTime', header: 'Total Time', cardLabel: 'Total', helpKey: 'attendance.myList.col.totalTime', render: r => r.attendance ? formatDuration(r.attendance.startTime, r.attendance.endTime) : '-' },
    { key: 'status', header: 'Status', priority: 1, helpKey: 'attendance.myList.col.status', render: r => <AttendanceStatusChip status={r.status} /> },
    { key: 'shift', header: 'Shift', helpKey: 'attendance.myList.col.shift', render: r => r.attendance?.shiftCode ? <Chip label={r.attendance.shiftCode} size="small" /> : '-' },
    { key: 'instanceStatus', header: 'Instance Status', helpKey: 'attendance.myList.col.instanceStatus', render: r => r.attendance?.instanceStatus ? humanizeEnum(r.attendance.instanceStatus) : '-' },
    { key: 'late', header: 'Late', helpKey: 'attendance.myList.col.late', render: r => r.attendance?.isLate ? <Chip label={`${r.attendance.lateMinutes}m`} size="small" color="error" /> : '-' },
    { key: 'overtime', header: 'OT', helpKey: 'attendance.myList.col.overtime', render: r => (r.attendance?.overtimeMinutes ?? 0) > 0 ? <Chip label={`${r.attendance!.overtimeMinutes}m`} size="small" /> : '-' },
];

/**
 * The Location column, built per render because it closes over the work-site name lookup.
 *
 * Not in `attendanceColumns` itself: that array is module-scope and unconditional, while this
 * column is only ever shown when the loaded rows actually carry location data.
 */
const locationColumn = (siteNamesById: Map<string, string>): Column<AttendanceRow> => ({
    key: 'location',
    header: 'Location',
    helpKey: 'attendance.myList.col.location',
    render: r => r.attendance ? (
        <LocationStatusChip
            status={r.attendance.locationStatus}
            distanceMeters={r.attendance.distanceMeters}
            siteName={r.attendance.workSiteId ? siteNamesById.get(r.attendance.workSiteId) : undefined}
            latitude={r.attendance.latitude}
            longitude={r.attendance.longitude}
            accuracyMeters={r.attendance.locationAccuracyMeters}
            isMock={r.attendance.isMockLocation}
        />
    ) : null,
});

/**
 * The column model for one render: the ten fixed columns, plus Location appended last when the
 * loaded rows carry location data.
 *
 * `attendanceColumns` stays exported and unconditional so the parity test can keep asserting the
 * ordered ten-key list — the thing that guards against a silent reorder. This factory is what the
 * page renders, and is pinned separately in both directions (present / absent).
 */
export const attendanceColumnsFor = (
    hasLocationData: boolean,
    siteNamesById: Map<string, string>,
): Column<AttendanceRow>[] =>
    hasLocationData ? [...attendanceColumns, locationColumn(siteNamesById)] : attendanceColumns;

const AttendanceList: React.FC = () => {
    const [attendanceData, setAttendanceData] = useState<AttendanceResponse | null>(null);
    const [leaveData, setLeaveData] = useState<LeaveApplicationResponse | null>(null);
    const [holidayData, setHolidayData] = useState<HolidayResponse | null>(null);
    const [loading, setLoading] = useState(true);
    const [hasAttendanceToday, setHasAttendanceToday] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [attendanceButtonEnabled, setAttendanceButtonEnabled] = useState(true);
    const [weekendDays, setWeekendDays] = useState<WeekDay[]>(FALLBACK_WEEKEND);
    const [selectedShiftId, setSelectedShiftId] = useState<string>('');
    const toast = useToast();
    const [startDate, setStartDate] = useState<Date>(subDays(new Date(), 30)); // Default to 30 days ago
    const [endDate, setEndDate] = useState<Date>(new Date()); // Default to today
    const [dateRange, setDateRange] = useState<string[]>([]);
    const { shifts } = useShiftLookup();
    const [siteNamesById, setSiteNamesById] = useState<Map<string, string>>(new Map());
    const [flaggedOnly, setFlaggedOnly] = useState(false);
    // An ordinary employee holds none of these keys: skip the calls instead of firing three 403s per visit.
    const canViewAllAttendance = usePermission('ATTENDANCE_VIEW_ALL');
    const canViewManagerBoard = usePermission('DASHBOARD_VIEW_MANAGER');
    const canReadSites = canViewAllAttendance || canViewManagerBoard;
    const canReadSettings = usePermission('INSTITUTION_SETTINGS_VIEW');

    useEffect(() => {
        fetchData();
    }, [startDate, endDate]);

    useEffect(() => {
        if (!canReadSites) return;
        // Fetched once and reused for every row — never per row. WorkSiteService.list
        // is paginated and clamped to 100 by the client service; an institution with
        // more than 100 sites will not have every name resolved here. Rows referencing
        // an unresolved site fall back to a distance-only label rather than a raw id.
        const fetchSiteNames = async () => {
            try {
                const page = await WorkSiteService.list(0, 100);
                setSiteNamesById(new Map(page.content.map(site => [site.id as string, site.name])));
            } catch {
                // Non-fatal: rows simply fall back to a distance-only location label.
            }
        };
        fetchSiteNames();
    }, [canReadSites]);

    useEffect(() => {
        if (!canReadSettings) return;
        const fetchSettings = async () => {
            try {
                const institutionId = AuthService.getSelectedInstitution();
                if (!institutionId) return;
                const settings = await InstitutionSettingsService.getSettings(institutionId);
                if (settings) {
                    setAttendanceButtonEnabled(settings.isAttendanceButtonEnabled);
                }
            } catch {
                // Settings may not exist yet, default to enabled
            }
        };
        fetchSettings();
    }, [canReadSettings]);

    // Weekend days are readable by every member (unlike the full settings), so employees and
    // department heads see the same Weekend/Absent labels as the admin. Falls back to Friday only.
    useEffect(() => {
        const fetchWeekend = async () => {
            try {
                const institutionId = AuthService.getSelectedInstitution();
                if (!institutionId) return;
                const days = await InstitutionSettingsService.getWeekendDays(institutionId);
                if (days.length > 0) setWeekendDays(days);
            } catch {
                // Keep the Friday-only fallback.
            }
        };
        fetchWeekend();
    }, []);

    useEffect(() => {
        if (startDate && endDate) {
            // Generate array of all dates in range
            const range = eachDayOfInterval({
                start: startOfDay(startDate),
                end: endOfDay(endDate)
            });

            // Format dates to match the format used in attendances
            const formattedDates = range.map(date => format(date, 'yyyy-MM-dd'));
            setDateRange(formattedDates.reverse()); // Most recent dates first
        }
    }, [startDate, endDate]);

    const fetchData = async () => {
        try {
            setLoading(true);
            // Fetch attendance data with date filters
            const { data, hasTodayAttendance } = await AttendanceService.getLoggedInUserAttendanceList(startDate, endDate);
            setAttendanceData(data);
            setHasAttendanceToday(hasTodayAttendance);

            // Fetch leave data
            const leaveResponse = await LeaveService.getLeaveApplications(0, 100);
            setLeaveData(leaveResponse);

            // Fetch holiday data
            const holidayResponse = await HolidayService.getHolidaysByInstitution();
            setHolidayData(holidayResponse);
        } catch (err) {
            toast.showApiError(err, 'Failed to fetch data');
        } finally {
            setLoading(false);
        }
    };

    const handleCheckIn = async () => {
        try {
            setSubmitting(true);
            await AttendanceService.clockIn();
            await fetchData();
        } catch (err) {
            toast.showApiError(err, 'Failed to check in');
        } finally {
            setSubmitting(false);
        }
    };

    const handleCheckOut = async () => {
        try {
            setSubmitting(true);
            await AttendanceService.clockOut();
            await fetchData();
        } catch (err) {
            toast.showApiError(err, 'Failed to check out');
        } finally {
            setSubmitting(false);
        }
    };

    const formatDate = (dateString: string) => {
        return format(parseISO(dateString), 'dd-MM-yyyy');
    };

    const getStatus = (date: string): string => {
        const dayName = JS_DAY_TO_NAME[parseISO(date).getDay()];
        if (weekendDays.includes(dayName)) {
            return 'Holiday: Weekend';
        }

        // Check if it's a regular attendance day
        if (attendanceData?.content.some(attendance => attendance.date === date)) {
            return 'Present';
        }

        // Check if it's a leave day
        const leaveApplication = leaveData?.content.find(leave =>
            leave.leaveStatus === LeaveStatus.Approved &&
            isWithinInterval(parseISO(date), {
                start: parseISO(leave.startDate),
                end: parseISO(leave.endDate)
            })
        );

        if (leaveApplication) {
            return leaveApplication.leaveType;
        }

        // Check if it's a holiday
        const holiday = holidayData?.content.find(h =>
            isWithinInterval(parseISO(date), {
                start: parseISO(h.startDate),
                end: parseISO(h.endDate)
            })
        );

        if (holiday) {
            return `Holiday: ${holiday.name}`;
        }

        // If no attendance, leave, or holiday, mark as Absent
        return 'Absent';
    };

    const getAttendanceByDate = (date: string) => {
        return attendanceData?.content.find(attendance => attendance.date === date);
    };

    if (loading) return <Typography>Loading...</Typography>;
    if (!attendanceData) return <Typography>No attendance data found</Typography>;

    // Hide the whole column when no row on the page has a locationStatus, so a
    // tenant that never enabled the feature sees exactly the table they see today.
    const hasLocationData = attendanceData.content.some(a => !!a.locationStatus);

    const rows: AttendanceRow[] = dateRange
        .filter(date => {
            if (!selectedShiftId) return true;
            return getAttendanceByDate(date)?.shiftId === selectedShiftId;
        })
        .filter(date => {
            // Gated on hasLocationData, not on flaggedOnly alone. The
            // checkbox only renders when the loaded rows carry location
            // data, so changing the date range to a span with none
            // unmounts it while flaggedOnly stays true — every row would
            // then be filtered out with no visible control to undo it,
            // and only a reload would recover.
            if (!flaggedOnly || !hasLocationData) return true;
            const attendance = getAttendanceByDate(date);
            // A simulated-location punch is the single most suspicious signal
            // in the system — it must never be filtered out of the flagged view,
            // even when the fabricated coordinates land INSIDE the fence.
            return attendance?.locationStatus === 'OUTSIDE' || !!attendance?.isMockLocation;
        })
        .map(date => ({
            rawDate: date,
            date: formatDate(date),
            day: format(parseISO(date), 'EEEE'),
            status: getStatus(date),
            attendance: getAttendanceByDate(date),
        }));

    return (
        <Box data-help-section="attendance.mine" sx={{ padding: { xs: 0, sm: 3 } }}>
            <Box sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                justifyContent: 'space-between',
                alignItems: { xs: 'stretch', sm: 'center' },
                gap: 2,
                marginBottom: 2,
            }}>
                <Box>
                    <Typography variant="h5">
                        Attendance List
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        Times shown in your local timezone ({Intl.DateTimeFormat().resolvedOptions().timeZone})
                    </Typography>
                </Box>
                <Box>
                    {attendanceButtonEnabled && (
                        !hasAttendanceToday ? (
                            <Button data-help="attendance.checkIn"
                                variant="contained"
                                color="primary"
                                onClick={handleCheckIn}
                                disabled={submitting}
                                sx={{ marginRight: { xs: 0, sm: 1 }, width: { xs: '100%', sm: 'auto' } }}
                            >
                                Check In
                            </Button>
                        ) : (
                            <Button data-help="attendance.checkOut"
                                variant="contained"
                                color="secondary"
                                onClick={handleCheckOut}
                                disabled={submitting}
                                sx={{ width: { xs: '100%', sm: 'auto' } }}
                            >
                                Check Out
                            </Button>
                        )
                    )}
                </Box>
            </Box>

            {/* Date Filter Controls */}
            <Grid data-help-section="attendance.myList.filters" container spacing={2} sx={{ marginBottom: 3 }}>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <TextField
                        fullWidth
                        data-help="attendance.myList.startDate"
                        label="Start Date"
                        type="date"
                        value={format(startDate, 'yyyy-MM-dd')}
                        onChange={(e) => setStartDate(new Date(e.target.value))}
                        InputLabelProps={{ shrink: true }}
                        sx={{ gridColumn: 'span 4' }}
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <TextField
                        fullWidth
                        data-help="attendance.myList.endDate"
                        label="End Date"
                        type="date"
                        value={format(endDate, 'yyyy-MM-dd')}
                        onChange={(e) => setEndDate(new Date(e.target.value))}
                        InputLabelProps={{ shrink: true }}
                        sx={{ gridColumn: 'span 4' }}
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <SearchableSelect
                        options={shifts.filter(s => s.isActive)}
                        value={selectedShiftId}
                        onChange={(id) => setSelectedShiftId(id)}
                        getOptionLabel={s => `${s.code} – ${s.name}`}
                        data-help="attendance.myList.shift"
                        label="Shift"
                        placeholder="All shifts"
                        sx={{ minWidth: 160 }}
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <Button
                        data-help="attendance.myList.apply"
                        variant="contained"
                        color="primary"
                        onClick={fetchData}
                        sx={{ mt: { xs: 0, md: 1 } }}
                    >
                        Apply Filters
                    </Button>
                </Grid>
                {hasLocationData && (
                    <Grid>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={flaggedOnly}
                                    onChange={(e) => setFlaggedOnly(e.target.checked)}
                                />
                            }
                            data-help="attendance.myList.flaggedOnly"
                            label="Flagged only"
                        />
                    </Grid>
                )}
            </Grid>

            <ResponsiveTable<AttendanceRow> data-help-section="attendance.myList.table"
                pageKey="attendance.myList"
                columns={attendanceColumnsFor(hasLocationData, siteNamesById)}
                rows={rows}
                getRowKey={r => r.rawDate}
                emptyMessage="No attendance in this date range"
                renderCard={row => (
                    <>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                            <Box>
                                <Box sx={{ fontSize: '1rem', fontWeight: 600 }}>{row.date}</Box>
                                <Box sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                                    {row.day}{row.attendance?.shiftCode ? ` · ${row.attendance.shiftCode}` : ''}
                                </Box>
                            </Box>
                            <AttendanceStatusChip status={row.status} />
                        </Box>
                        {row.attendance && (
                            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '7px 12px', mt: 1.25 }}>
                                <Box><Box sx={labelSx}>In</Box>{formatTime(row.attendance.startTime)}</Box>
                                <Box><Box sx={labelSx}>Out</Box>{formatEndTime(row.attendance.startTime, row.attendance.endTime)}</Box>
                                <Box><Box sx={labelSx}>Total</Box>{formatDuration(row.attendance.startTime, row.attendance.endTime)}</Box>
                                {row.attendance.isLate
                                    ? <Box><Box sx={labelSx}>Late by</Box>{row.attendance.lateMinutes}m</Box>
                                    : (row.attendance.overtimeMinutes ?? 0) > 0
                                        ? <Box><Box sx={labelSx}>Overtime</Box>{row.attendance.overtimeMinutes}m</Box>
                                        : null}
                            </Box>
                        )}
                        {/* Card mode is the phone DEFAULT, so the location flag has to be reachable
                            here too — otherwise someone who just ticked "Flagged only" cannot see
                            the very thing they filtered for. The chip carries its own label, so it
                            needs no label row of its own. */}
                        {row.attendance?.locationStatus && (
                            <Box sx={{ mt: 1 }}>
                                <LocationStatusChip
                                    status={row.attendance.locationStatus}
                                    distanceMeters={row.attendance.distanceMeters}
                                    siteName={row.attendance.workSiteId
                                        ? siteNamesById.get(row.attendance.workSiteId)
                                        : undefined}
                                    latitude={row.attendance.latitude}
                                    longitude={row.attendance.longitude}
                                    accuracyMeters={row.attendance.locationAccuracyMeters}
                                    isMock={row.attendance.isMockLocation}
                                />
                            </Box>
                        )}
                    </>
                )}
            />
        </Box>
    );
};

export default AttendanceList;
